import {useRouter} from 'next/router';
import React, {useCallback, useEffect, useState} from 'react';
import Head from 'next/head';
import {
  Box,
  NFTDetailsPanel,
  MusicTrackPlayer,
  Typography,
  VisibilityBox,
  MusicTrackSlider,
  ArtistAboutDetails,
  BasicTextScroll,
  HorizontalDivider,
  Modal,
  GridContainerWithTitle,
  theme,
  EmailSignupForm,
} from '@ttx/design-system';
import {useBreakpoint} from '@ttx/design-system/src/use-breakpoint';
import {GetServerSideProps} from 'next';
import {BigNumber, formatEther, parseEther} from '@ttx/core';
import axios from 'axios';
import {NextPageWithPageLayoutOptions} from '../../components/page-layout/types';

import {useMusicPlayer} from '../../contexts/music-player-context/music-player-context';
import {useEmailSignup} from '../../hooks/use-email-signup';
import {SEO} from '../../components/seo';
import {RemixCardScroll} from '../../components/remix-card-scroll';
import {post} from '../../server/fetch/fetch';
import {endpoint} from '../../server/fetch/ttx-api-server';
import {
  Collection,
  NftToken,
  StemzCollectionListResponse,
} from '../../server/router/collection/types';
import {AboutArtistDocument, ArtistRecord} from '../../graphql/generated';
import {request} from '../../graphql/api';
import {PurchaseCta} from '../../components/purchase-or-download-cta/purchase-cta';
import {useMint} from '../../hooks/use-mint';
import {WalletNetwork} from '../../contexts/authentication-context/types';
import {BlockchainPropsWrapper} from '../../components/blockchain-props-wrapper';
import {useTraxxStemzContract} from '../../components/blockchain-props-wrapper/use-traxxstemz-contract';
import {CheckoutButtonType} from '../../hooks/use-checkout';
import {
  LocalStroageUtil,
  LOCAL_STORAGE_KEY,
} from '../../utils/local-storage-utils';
import {traxxStemzSlug} from '../../utils/consts';
import {useGeoInfo} from '../../hooks/use-geo-info';
import {useAuthenticationContext} from '../../contexts/authentication-context';

interface Credit {
  label: string;
  content: string;
}

interface TraxxStemzPageProps {
  id?: string;
  url?: string;
  title?: string;
  tokenId?: number;
  description?: string;
  image?: string;
  collectionId?: string;
  artist?: ArtistRecord;
  trackName?: string;
  displayName?: string;
  contractAddress?: string;
  ipAddress?: string;
  credits: Credit[] | null;
  collectionData?: Collection;
  data?: NftToken;
  country: string;
}

const TraxxStemzPage: NextPageWithPageLayoutOptions<TraxxStemzPageProps> = (
  props,
) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);
  const {currentBreakpoint} = useBreakpoint();
  const isMobile = currentBreakpoint?.toString() === theme.breakpoints[0];

  const {userCountry, userIPAddress} = useGeoInfo();

  const {walletProvider} = useAuthenticationContext();

  const {
    isPlaying,
    isLoading,
    duration,
    currentTime,
    setCurrentTime,
    audioPlayer,
    togglePlayPause,
  } = useMusicPlayer();

  const {
    artist,
    displayName,
    trackName,
    id,
    url,
    ipAddress,
    collectionData,
    data,
  } = props;

  const localArtist =
    data?.properties.artist || collectionData?.creatorDetails.user.displayName;

  const {mintPrice, tokenLimit, availableSupply} = useTraxxStemzContract({
    network: collectionData?.blockchainNetworkDetail.network as WalletNetwork,
    isTraxxStemz: true,
  });

  const {
    monetaryValues,
    modalVariant,
    modalVariantComponent,
    setModalVariant,
    handleStartCheckoutFlow,
  } = useMint({
    id,
    network: collectionData?.blockchainNetworkDetail.network,
    ipAddress: userIPAddress,
    url: `${url}/traxxstemz/${traxxStemzSlug}`,
    trackName,
    artist: artist?.artistName || '',
    imageSrc: props.image,
    buyoutPrice: parseEther(formatEther(mintPrice || '0')),
    currencySymbol: 'ETH',
    quantity: BigNumber.from(tokenLimit || '0'),
    isAllowedUser: true,
    whiteList: [],
    country: userCountry,
  });

  const {onSignupSubmit} = useEmailSignup({
    collectionId: props?.collectionId,
    artistSlug: String(artist?.slug),
  });

  const paymentIdFromUrl = router.query.paymentId;
  const onModalClose = () => {
    setModalVariant(null);
    if (paymentIdFromUrl) {
      LocalStroageUtil.setItem(LOCAL_STORAGE_KEY.PAYMENT_ID, paymentIdFromUrl);
    }
  };

  let btnType = CheckoutButtonType.LOADING;
  if (!Number(mintPrice)) {
    btnType = CheckoutButtonType.LOADING;
  } else if (mintPrice && tokenLimit && availableSupply) {
    btnType = CheckoutButtonType.MINT;
  }

  if (walletProvider === 'venly') {
    btnType = CheckoutButtonType.PURCHASE_DISABLE;
  }

  const ctaButton = (
    <PurchaseCta
      buttonType={btnType}
      onPressSignUp={() => setShowModal(true)}
      onPressPurchase={handleStartCheckoutFlow}
    />
  );

  const artistDetails = (
    <ArtistAboutDetails
      thumbnail={artist?.image?.url}
      artistName={artist?.artistName}
      bio={artist?.bio}
      instagramHandle={artist?.instagram}
      twitterHandle={artist?.twitter}
      youtubeHandle={artist?.youtube}
      withGoToProfileButton={!!artist?.slug}
      navigateToProfile={() => router.push(`/artists/${artist?.slug}`)}
    />
  );

  return (
    <Box width="100%" height="100%">
      <Head>
        <title>
          {trackName} by {displayName}
        </title>
      </Head>
      <SEO
        url={`${props.url}/traxxstemz/${traxxStemzSlug}`}
        title={`${trackName} by ${displayName}`}
        description={props.description}
        image={props.image}
      />
      <Box height="100%">
        {data && collectionData ? (
          <BlockchainPropsWrapper
            multiCopy={collectionData.multiCopy}
            network={collectionData.blockchainNetworkDetail.network}
            nftToken={data}
            type={5}
          >
            <NFTDetailsPanel
              dateAdded={new Date(data.createdAtUTC)}
              src={props.image || ''}
              network={collectionData.blockchainNetworkDetail.network}
              currencySymbol={monetaryValues.crypto.currencySymbol}
              nativePrice={
                monetaryValues.crypto.totalPerNFT
                  ? (Number(monetaryValues.crypto.totalPerNFT) * 5).toString()
                  : ''
              }
              usdPrice={
                monetaryValues.cash.totalPerNFT
                  ? (
                      Number(
                        monetaryValues.cash.totalPerNFT?.replace('$', ''),
                      ) * 5
                    )
                      .toFixed(2)
                      .toString()
                  : ''
              }
              ctaComponent={ctaButton}
              description={props.description}
              totalSupply={tokenLimit || '0'}
              state="traxxStemz"
              credits={
                props.credits
                  ? Object.keys(props.credits).map((key) => {
                      return {
                        key,
                        label: key,
                        content: (props.credits as any)[key],
                      };
                    })
                  : null
              }
              musicPlayer={
                <MusicTrackPlayer
                  artist={artist?.artistName || displayName}
                  songTitle={trackName}
                  isPlaying={isPlaying}
                  isLoading={isLoading}
                  duration={duration}
                  currentTime={currentTime}
                  togglePlayPause={togglePlayPause}
                  hidePlayer
                  musicPlayerDisabled
                />
              }
              musicSlider={
                <MusicTrackSlider
                  isPlaying={isPlaying}
                  duration={duration}
                  currentTime={currentTime}
                  setCurrentTime={setCurrentTime}
                  audioPlayer={audioPlayer}
                />
              }
              availableQty={availableSupply || '0'}
              artist={localArtist}
              isMobileTS
              musicPlayerDisabled
            />
          </BlockchainPropsWrapper>
        ) : null}
        <VisibilityBox show={[false, true, false]}>{ctaButton}</VisibilityBox>
        <VisibilityBox show={[false, true, false]}>
          <HorizontalDivider />
        </VisibilityBox>
        <Box>
          <BasicTextScroll text="Buy 1, get 4 for free - " />
          <HorizontalDivider />
          <Box paddingY="seven" paddingX="lg">
            <Typography textStyle={['h2', 'h1']} color="textDefault">
              <p>
                Graffiti6 Revolution TraxxStemz is a collection of 3030 NFTs
                created from randomly combined layers of visual art and music,
                representing an entirely new format for art. The original track
                “Revolution” by Graffiti6 has been remixed by 4 incredible
                talents– Paul Woolford, Flux Pavilion, Calyx & Teebee, Oden and
                Fatzo– and then TokenTraxx&apos;s TraxxStemz technology is used
                to recombine the audio layers (or stems) from the original and
                remixes into totally new pieces of music, time-stretched and
                pitch-shifted to sound fantastic! The 3030 NFTs are generated
                from 2,000,000 potential combinations across 9 different traits,
                such as drums, bass, FX and lead vocal.
              </p>
              <p>
                Each audio mix is accompanied by randomly combined artwork
                generated using the same technology, featuring stunning 3D heart
                visuals from Graffiti6 member and visual artist, Jimi Crayon.
                The 3030 artworks are generated from 50,000 potential
                combinations of the heart, its accessory and an accent!
              </p>
              <p>
                Graffiti6 is the first artist to make use of TraxxStemz
                technology with this original track, “Revolution”. Graffiti6 is
                a collaboration of Grammy winning singer songwriter Jamie Scott,
                graffiti artist Jimi Crayon and the genesis drop of award
                winning music producer, songwriter and TokenTraxx co-founder
                TommyD.
              </p>
              <p>
                Future features and perks will be unlocked by the TraxxStemz
                community through our forth-coming utility roadmap.
              </p>
            </Typography>
          </Box>
          <Box paddingX="lg" mt="twentyFour">
            <Typography textStyle={['h2', 'h1']} color="textDefault">
              The Mixes
            </Typography>
          </Box>
          <Box mt="lg">
            <RemixCardScroll
              remixCardData={[
                {
                  artist: 'Graffiti6',
                  songTitle: 'The Original Mix',
                  imageSrc: '/graffiti6-remix-assets/original-mix.jpg',
                  audioSrc: '/graffiti6-remix-assets/original-mix.mp3',
                },
                {
                  artist: 'Paul Woolford',
                  songTitle: 'Paul Woolford Remix',
                  imageSrc: '/graffiti6-remix-assets/paul-woolford.jpg',
                  audioSrc: '/graffiti6-remix-assets/paul-woolford.mp3',
                },
                {
                  artist: 'Flux Pavilion',
                  songTitle: 'Flux Pavilion Remix',
                  imageSrc: '/graffiti6-remix-assets/flux-pavilion.jpg',
                  audioSrc: '/graffiti6-remix-assets/flux-pavilion.mp3',
                },
                {
                  artist: 'Calyx & TeeBee',
                  songTitle: 'Calyx & TeeBee Remix',
                  imageSrc: '/graffiti6-remix-assets/calyx.jpg',
                  audioSrc: '/graffiti6-remix-assets/calyx.mp3',
                },
                {
                  artist: 'Oden & Fatzo',
                  songTitle: 'Oden & Fatzo Remix',
                  imageSrc: '/graffiti6-remix-assets/oden.jpg',
                  audioSrc: '/graffiti6-remix-assets/oden.mp3',
                },
              ]}
            />
          </Box>
        </Box>
        <VisibilityBox show={!!artist?.bio}>
          <Box paddingTop="fourteen">
            <Box p="five">
              <Typography color="textDefault" textStyle={['h2', 'h1']}>
                About the artist
              </Typography>
            </Box>
            {artistDetails}
            <VisibilityBox show={[false, true, false]}>
              <HorizontalDivider />
            </VisibilityBox>
          </Box>
        </VisibilityBox>
        <VisibilityBox show={[true, false, false]}>
          <Box css={{position: 'sticky'} as any} bottom={0} width="100%">
            {ctaButton}
          </Box>
        </VisibilityBox>
      </Box>
      <Modal
        visible={!!modalVariant && !modalVariant.includes('awaiting-signature')}
        onModalClose={onModalClose}
        closeOnOverlay
        __useReactNativeModal={false}
      >
        <Box
          flex={1}
          w="100%"
          alignItems="center"
          justifyContent="center"
          paddingX="two"
        >
          {modalVariantComponent}
        </Box>
      </Modal>
      <Modal
        visible={showModal}
        onModalClose={() => setShowModal(false)}
        closeOnOverlay
        __useReactNativeModal={false}
      >
        <Box
          flex={1}
          w="100%"
          alignItems="center"
          justifyContent="center"
          paddingX="two"
        >
          <Box backgroundColor={isMobile ? 'surfaceDark' : undefined}>
            <GridContainerWithTitle
              title="Revolution by Graffiti6 — Sign up for updates"
              onModalClose={() => setShowModal(false)}
              maxWidth={568}
            >
              <EmailSignupForm
                onSignupSubmit={onSignupSubmit}
                content1="Don't miss out on our first ever TraxxStemz drop, with uniquely generated tracks from the Revolution remixes' audio stems."
                content2="Sign up for updates to get exclusive access to the drop, giveaways, and more."
                apiKey={process.env.NEXT_PUBLIC_RECAPTCHA}
              />
            </GridContainerWithTitle>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id = context?.query?.id;
  const ipAddress = context?.req.socket.remoteAddress;
  const country = 'D_UK';

  const collection = (await post(`${endpoint}/collection/list`, {
    limit: 0,
    page: 1,
    creator: undefined,
    collectionStatus: ['minted', 'draft'],
    nftStatus: ['minted', 'draft'],
    isTraxxStemz: true,
  })) as {data: StemzCollectionListResponse};

  // try {
  //   const res = await axios.get(`${process.env.GEOLOCATION_API_URL}`);
  //   ipAddress = res.data.ip || ipAddress;
  //   country = res.data?.country_code || 'D_UK';
  // } catch (e) {
  //   ipAddress = context?.req.socket.remoteAddress;
  // }

  const collectionData = collection.data;

  const data = collection.data.nftTokenIds[0];

  const artistSlug = collectionData?.creatorDetails.user.slug;

  let artistResult;

  if (artistSlug) {
    artistResult = await request(AboutArtistDocument, {
      slug: artistSlug,
    });
  }

  return {
    props: {
      id,
      ipAddress,
      url: context?.req?.headers?.host || null,
      description: data?.description || null,
      image: data?.image || null,
      collectionId: collectionData?._id || null,
      artist: artistResult?.artist || null,
      trackName: data?.name || null,
      displayName: collectionData?.creatorDetails.user.displayName || null,
      credits: collectionData?.nftTokenIds[0].credits || null,
      tokenId: data?.tokenId || null,
      contractAddress: data?.contractAddress || null,
      collectionData: collectionData || null,
      data: data || null,
      country,
    },
  };
};

TraxxStemzPage.pageLayoutOptions = {
  showFooter: true,
  showMusicPlayer: false,
};

export default TraxxStemzPage;
